import Vue from 'vue'
import Router from 'vue-router'

import {
  createSignInPageGuard,
  createUserPageGuard,
  createHomePageGuard
} from './support'

// Playground
import UiElementsPage from '@/pages/playground/UiElementsPage'
import TextPage from '@/pages/playground/TextPage'

// App
import HomePage from '@/pages/HomePage'
import PageNotFound from '@/pages/PageNotFound'

// Auth
import SignUpPage from '@/pages/SignUpPage'
import SignInPage from '@/pages/SignInPage'
import PasswordResetPage from '@/pages/PasswordResetPage'
import ConfirmPasswordResetPage from '@/pages/ConfirmPasswordResetPage'

// Content
import ContentPage from '@/pages/contents/ContentPage'

// Folders
import Folders from '@/pages/folders/FoldersPage'
import FoldersDetail from '@/pages/folders/FoldersDetailPage'

// Teams
import Teams from '@/pages/teams/TeamsPage'
import Team from '@/pages/teams/TeamPage'
import TeamSettings from '@/pages/teams/TeamSettingsPage'
import TeamMembers from '@/pages/teams/TeamMembersPage'

// Stream
import Stream from '@/pages/stream/StreamPage.vue'

// Settings
import ProfileSettings from '@/pages/settings/ProfileSettings'
import DestroyProfileSettings from '@/pages/settings/DestroyProfileSettings'
import EmailSettings from '@/pages/settings/EmailSettings'
import PasswordSettings from '@/pages/settings/PasswordSettings'
import SettingsPage from '@/pages/settings/SettingsPage'

// Complete Profile
import CompleteProfile from '@/pages/CompleteProfilePage'

// Complete Profile
import UpdatePassword from '@/pages/UpdatePasswordPage'

// Search
import Search from '@/pages/search/SearchPage'

// Tags
import Tags from '@/pages/tags/TagsPage'

// Client
import ClientPage from '@/pages/clients/ClientPage'
import ClientTags from '@/pages/clients/ClientTagsPage'
import ClientTheme from '@/pages/clients/ClientThemePage'
import ClientSettings from '@/pages/clients/ClientSettingsPage'
import ClientMembers from '@/pages/clients/ClientMembersPage'
import ClientMembersDetail from '@/pages/clients/ClientMembersDetailPage'
import ClientInvitations from '@/pages/clients/ClientInvitationsPage'

// Invitations
import ConfirmInvitationPage from '@/pages/invitations/ConfirmInvitationPage'

// Newsletter
import NewsletterPage from '@/pages/newsletter/NewsletterPage'
import LatestNewsletterPage from '@/pages/newsletter/LatestNewsletterPage'

// Static
import StaticAboutEN from '@/pages/static/en/About'
import StaticHowToUseEN from '@/pages/static/en/HowToUse'
import StaticImprintEN from '@/pages/static/en/Imprint'
import StaticPrivacyEN from '@/pages/static/en/Privacy'
import StaticTermsEN from '@/pages/static/en/Terms'
import StaticContactEN from '@/pages/static/en/Contact'

import StaticAboutDE from '@/pages/static/de/About'
import StaticHowToUseDE from '@/pages/static/de/HowToUse'
import StaticImprintDE from '@/pages/static/de/Imprint'
import StaticPrivacyDE from '@/pages/static/de/Privacy'
import StaticTermsDE from '@/pages/static/de/Terms'
import StaticContactDE from '@/pages/static/de/Contact'

// Webview
import WebviewInit from '@/pages/webview/Init'
import WebviewAuth from '@/pages/webview/Auth'

Vue.use(Router)

export const createRouter = store => {


  return new Router({
    mode: 'history',
    scrollBehavior: (to, from, savedPosition) => {
      return new Promise((resolve, reject) => {
        let position = {}

        if (to.hash) {
          position.selector = to.hash,
          position.offset = {
            x: 0,
            y: 80
          }
        } else if (to.matched.some(m => m.meta.scrollToTop)) {
          position.x = 0
          position.y = 0
        } else if (savedPosition) {
          position = savedPosition
        }

        resolve(position)
      })
    },
    routes: [
      {
        beforeEnter: createHomePageGuard(store),
        component: HomePage,
        name: 'Home',
        path: '/'
      },
      // {
      //   beforeEnter: createSignInPageGuard(store),
      //   component: SignUpPage,
      //   name: 'SignUpPage',
      //   path: '/sign-up'
      // },
      {
        beforeEnter: createSignInPageGuard(store),
        component: SignInPage,
        name: 'SignInPage',
        path: '/sign-in'
      },
      {
        component: ConfirmInvitationPage,
        name: 'ConfirmInvitation',
        path: '/invitations/:token'
      },
      {
        component: NewsletterPage,
        name: 'Newsletter',
        path: '/newsletter/:id'
      },
      {
        component: PasswordResetPage,
        name: 'PasswordResetPage',
        path: '/password-reset'
      },
      {
        component: ConfirmPasswordResetPage,
        name: 'ConfirmPasswordResetPage',
        path: '/password_resets/:token'
      },
      {
        beforeEnter: createUserPageGuard(store),
        component: ContentPage,
        name: 'ContentPage',
        path: '/contents/:contentId',
        meta: {
          scrollToTop: true
        }
      },
      {
        beforeEnter: createUserPageGuard(store),
        component: Folders,
        name: 'Folders',
        path: '/foldaz',
        meta: {
          scrollToTop: true
        }
      },
      {
        beforeEnter: createUserPageGuard(store),
        component: Stream,
        name: 'Stream',
        path: '/stream'
      },
      {
        beforeEnter: createUserPageGuard(store),
        component: Search,
        name: 'Search',
        path: '/search'
      },
      {
        beforeEnter: createUserPageGuard(store),
        component: Tags,
        name: 'Tags',
        path: '/tag/:tag',
        meta: {
          scrollToTop: true
        }
      },
      {
        beforeEnter: createUserPageGuard(store),
        component: FoldersDetail,
        name: 'FoldersDetail',
        path: '/foldaz/:folderId',
        meta: {
          scrollToTop: true
        }
      },
      {
        component: LatestNewsletterPage,
        name: 'LatestNewsletterPage',
        path: '/foldaz/:folderId/newsletter',
        meta: {
          scrollToTop: true
        }
      },
      {
        beforeEnter: createUserPageGuard(store),
        component: Teams,
        name: 'Teams',
        path: '/teams',
        meta: {
          scrollToTop: true
        }
      },
      {
        beforeEnter: createUserPageGuard(store),
        component: Team,
        path: '/team/:teamId',
        children: [
          {
            beforeEnter: createUserPageGuard(store),
            path: 'settings',
            component: TeamSettings,
            name: 'TeamSettings'
          },
          {
            beforeEnter: createUserPageGuard(store),
            path: 'members',
            component: TeamMembers,
            name: 'TeamMembers'
          }
        ]
      },
      {
        beforeEnter: createUserPageGuard(store),
        component: ClientPage,
        path: '/client',
        name: 'Client',
        children: [
          {
            beforeEnter: createUserPageGuard(store),
            path: 'settings',
            component: ClientSettings,
            name: 'ClientSettings'
          },
          {
            path: 'tags',
            component: ClientTags,
            name: 'ClientTags'
          },
          {
            path: 'theme',
            component: ClientTheme,
            name: 'ClientTheme'
          },
          {
            path: 'members',
            component: ClientMembers,
            name: 'ClientMembers'
          },
          {
            path: 'members/:memberId',
            component: ClientMembersDetail,
            name: 'ClientMembersDetail'
          },
          {
            path: 'invitations',
            component: ClientInvitations,
            name: 'ClientInvitations'
          }
        ]
      },
      {
        path: '/complete-profile',
        name: 'CompleteProfile',
        component: CompleteProfile
      },
      {
        path: '/update-password',
        name: 'UpdatePassword',
        component: UpdatePassword
      },
      {
        beforeEnter: createUserPageGuard(store),
        component: SettingsPage,
        path: '/settings',
        children: [
          {
            beforeEnter: createUserPageGuard(store),
            path: 'profile',
            component: ProfileSettings,
            name: 'ProfileSettings'
          },
          {
            path: 'password',
            component: PasswordSettings,
            name: 'PasswordSettings'
          },
          {
            path: 'email',
            component: EmailSettings,
            name: 'EmailSettings'
          },
          {
            path: 'close-account',
            component: DestroyProfileSettings,
            name: 'DestroyProfileSettings'
          }
        ]
      },
      {
        path: '/playground/text-page',
        name: 'TextPage',
        component: TextPage
      },
      {
        path: '/playground/ui-elements',
        name: 'UiElementsPage',
        component: UiElementsPage
      },
      {
        path: '/playground/ui-elements-page2',
        name: 'UiElementsPage2',
        component: UiElementsPage
      },
      {
        path: '/playground/ui-elements-page3',
        name: 'UiElementsPage3',
        component: UiElementsPage
      },


      {
        path: '/en/about',
        name: 'StaticAboutEN',
        component: StaticAboutEN,
        meta: {
          scrollToTop: true
        }
      },
      {
        path: '/en/how-to-use',
        name: 'StaticHowToUseEN',
        component: StaticHowToUseEN,
        meta: {
          scrollToTop: true
        }
      },
      {
        path: '/en/imprint',
        name: 'StaticImprintEN',
        component: StaticImprintEN,
        meta: {
          scrollToTop: true
        }
      },
      {
        path: '/en/privacy',
        name: 'StaticPrivacyEN',
        component: StaticPrivacyEN,
        meta: {
          scrollToTop: true
        }
      },
      {
        path: '/en/terms',
        name: 'StaticTermsEN',
        component: StaticTermsEN,
        meta: {
          scrollToTop: true
        }
      },
      {
        path: '/en/contact',
        name: 'StaticContactEN',
        component: StaticContactEN,
        meta: {
          scrollToTop: true
        }
      },


      {
        path: '/de/about',
        name: 'StaticAboutDE',
        component: StaticAboutDE,
        meta: {
          scrollToTop: true
        }
      },
      {
        path: '/de/funktion',
        name: 'StaticHowToUseDE',
        component: StaticHowToUseDE,
        meta: {
          scrollToTop: true
        }
      },
      {
        path: '/de/impressum',
        name: 'StaticImprintDE',
        component: StaticImprintDE,
        meta: {
          scrollToTop: true
        }
      },
      {
        path: '/de/datenschutz',
        name: 'StaticPrivacyDE',
        component: StaticPrivacyDE,
        meta: {
          scrollToTop: true
        }
      },
      {
        path: '/de/nutzungsbedingungen',
        name: 'StaticTermsDE',
        component: StaticTermsDE,
        meta: {
          scrollToTop: true
        }
      },
      {
        path: '/de/kontakt',
        name: 'StaticContactDE',
        component: StaticContactDE,
        meta: {
          scrollToTop: true
        }
      },

      {
        component: WebviewInit,
        name: 'WebviewInit',
        path: '/webview/init',
        meta: {
          layout: 'blank'
        }
      },
      {
        component: WebviewAuth,
        name: 'WebviewAuth',
        path: '/webview/auth/:locale/:token',
        meta: {
          layout: 'blank'
        }
      },

      {
        path: '*',
        name: 'PageNotFound',
        component: PageNotFound
      }
    ]
  })
}



// WEBPACK FOOTER //
// ./src/router/index.js